import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { blogPosts } from "@/data/blog";

const BlogArticle = () => {
  const { slug } = useParams();
  const { language } = useLanguage();

  const postIndex = blogPosts.findIndex((p) => p.slug === slug);
  const post = blogPosts[postIndex];

  if (!post) return <Navigate to="/blog" replace />;

  const nextPost = blogPosts[postIndex + 1];
  const content = post.content[language] || post.content.fr;

  // Simple markdown-like rendering
  const renderContent = (md) => {
    return md.split("\n").map((line, i) => {
      // Headings
      if (line.startsWith("#### "))
        return <h4 key={i} className="text-lg font-bold mt-6 mb-2">{line.slice(5)}</h4>;
      if (line.startsWith("### "))
        return <h3 key={i} className="text-xl font-bold mt-8 mb-3">{line.slice(4)}</h3>;
      if (line.startsWith("## "))
        return <h2 key={i} className="text-2xl font-bold mt-10 mb-4">{line.slice(3)}</h2>;

      // Horizontal rule
      if (line.trim() === "---")
        return <hr key={i} className="my-8 border-foreground/10" />;

      // Table
      if (line.startsWith("|")) {
        // Skip separator rows
        if (line.includes("---")) return null;
        const cells = line.split("|").filter(Boolean).map((c) => c.trim());
        const isHeader = i > 0 && md.split("\n")[i + 1]?.includes("---");
        const Tag = isHeader ? "th" : "td";
        return (
          <tr key={i} className={isHeader ? "border-b-2 border-foreground/10" : "border-b border-foreground/5"}>
            {cells.map((cell, ci) => (
              <Tag key={ci} className={`py-2 px-4 text-left text-sm ${isHeader ? "font-bold" : "text-muted-foreground"}`}>
                {renderInline(cell)}
              </Tag>
            ))}
          </tr>
        );
      }

      // List items
      if (line.startsWith("- **"))
        return <li key={i} className="ml-4 mb-1 text-foreground leading-relaxed">{renderInline(line.slice(2))}</li>;
      if (line.startsWith("- "))
        return <li key={i} className="ml-4 mb-1 text-muted-foreground leading-relaxed">{renderInline(line.slice(2))}</li>;

      // Numbered list
      if (/^\d+\.\s/.test(line))
        return <li key={i} className="ml-4 mb-1 text-foreground leading-relaxed list-decimal">{renderInline(line.replace(/^\d+\.\s/, ""))}</li>;

      // Empty line
      if (line.trim() === "") return <div key={i} className="h-2" />;

      // Italic paragraph
      if (line.startsWith("*") && line.endsWith("*"))
        return <p key={i} className="text-muted-foreground italic leading-relaxed">{renderInline(line.slice(1, -1))}</p>;

      // Normal paragraph
      return <p key={i} className="text-foreground/80 leading-relaxed">{renderInline(line)}</p>;
    });
  };

  const renderInline = (text) => {
    // Bold
    const parts = text.split(/(\*\*[^*]+\*\*)/);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**"))
        return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
      return part;
    });
  };

  // Check if content has tables
  const hasTable = content.includes("| ");
  const tableStart = content.indexOf("| ");
  const tableEnd = content.indexOf("\n\n", tableStart);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={post.title[language] || post.title.fr}
        description={post.excerpt[language] || post.excerpt.fr}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        article={{ date: post.date, category: post.category }}
      />
      {/* Article JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title[language] || post.title.fr,
        description: post.excerpt[language] || post.excerpt.fr,
        image: post.image,
        datePublished: post.date,
        author: { "@type": "Organization", name: "Lacrea", url: "https://lacrea.fr" },
        publisher: { "@type": "Organization", name: "Lacrea", url: "https://lacrea.fr", logo: { "@type": "ImageObject", url: "https://lacrea.fr/favicon.svg" } },
        mainEntityOfPage: { "@type": "WebPage", "@id": `https://lacrea.fr/blog/${post.slug}` },
      }) }} />
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-10">
        <div className="flex items-center gap-4 mb-8 text-sm">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-semibold"
          >
            <Logo className="w-6 h-6" />
            <span>Retour à l'accueil Lacrea</span>
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Blog
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full">
            {post.category}
          </span>
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(post.date).toLocaleDateString(
              language === "fr" ? "fr-FR" : language === "es" ? "es-ES" : "en-GB",
              { day: "numeric", month: "long", year: "numeric" }
            )}
          </span>
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            {post.readTime}
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight"
        >
          {post.title[language] || post.title.fr}
        </motion.h1>
      </div>

      {/* Hero image */}
      {post.image && (
        <div className="max-w-3xl mx-auto px-6 mb-10">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-border shadow-md">
            <img
              src={post.image}
              alt={post.title[language] || post.title.fr}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-3xl mx-auto px-6 pb-16"
      >
        <div className="prose-custom">
          {renderContent(content)}
        </div>
      </motion.div>

      {/* CTA + Next article */}
      <div className="max-w-3xl mx-auto px-6 pb-24 space-y-8">
        {/* CTA */}
        <div className="bg-primary/5 rounded-2xl p-8 text-center border border-primary/10">
          <h3
            className="text-2xl font-bold mb-3"
          >
            {language === "fr"
              ? "Un projet en tête ?"
              : language === "es"
              ? "¿Un proyecto en mente?"
              : "Have a project in mind?"}
          </h3>
          <p className="text-muted-foreground mb-6">
            {language === "fr"
              ? "Discutons de votre projet — devis gratuit sous 48h."
              : language === "es"
              ? "Hablemos de tu proyecto — presupuesto gratis en 48h."
              : "Let's discuss your project — free quote within 48h."}
          </p>
          <Link to="/#contact">
            <Button className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg font-semibold">
              {language === "fr"
                ? "Demander un devis gratuit"
                : language === "es"
                ? "Solicitar presupuesto gratis"
                : "Get a Free Quote"}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Next article */}
        {nextPost && (
          <Link
            to={`/blog/${nextPost.slug}`}
            className="group flex items-center justify-between p-6 bg-card rounded-2xl border border-border shadow-md hover:shadow-lg transition-shadow"
          >
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                {language === "fr" ? "Article suivant" : language === "es" ? "Siguiente artículo" : "Next article"}
              </span>
              <h4 className="font-bold group-hover:text-primary transition-colors">
                {nextPost.title[language] || nextPost.title.fr}
              </h4>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default BlogArticle;
